import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { DIRECTED_MAX_CAST } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { castMessages as messages } from '../messages/castMessages';
import { CAST_UPLOAD_TYPES } from './castRules';
import type { ArtistPictureOffer } from './useArtistPictureChoice';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import styles from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CastAddCardProps {
  /** `card`: the last tile of the grid (b1). `empty`: the cast has no picture yet (b3). */
  variant: 'card' | 'empty';
  /** The cast has no room: the tile says so (b3 "The cast is full"). */
  isFull: boolean;
  /** No AI picture is left for this video: "Make a picture" is off. */
  noPicturesLeft: boolean;
  /** The consent box is not ticked: "Upload photos" and the drop are off, and the tile says why (review I3). */
  uploadLocked: boolean;
  disabled: boolean;
  onFiles: (files: File[]) => void;
  onMakePicture: () => void;
  /** The artist picture of the account, or null when it has none or the cast holds it. */
  artistPicture: ArtistPictureOffer | null;
  onUseArtistPicture: () => void;
}

const sparkle = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" strokeLinejoin="round" />
  </svg>
);

/**
 * "Add to the cast" (design b1 `.cc.add`, b3 empty cast, j1): drop photos, "Upload photos", "Make a
 * picture" and, when the artist has a picture, "Use my artist picture" (owner request 2026-10-19,
 * the small picture in the button). With a full cast it says "The cast is full". Until the consent
 * box is ticked, the upload is off (a picture that the AI makes and the artist picture need no tick).
 */
const CastAddCardComponent: React.FC<CastAddCardProps> = (props) => {
  const { variant, isFull, noPicturesLeft, uploadLocked, disabled, onFiles, onMakePicture } = props;
  const { artistPicture, onUseArtistPicture } = props;
  const uploadOff = disabled || uploadLocked;
  const intl = useIntl();
  const input = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  if (isFull) {
    return (
      <div className={clsx(styles.add, styles.addFull)} data-testid="cast-full">
        <span className={styles.addTitle}>{intl.formatMessage(messages.fullTitle)}</span>
        <span className={styles.addText}>{intl.formatMessage(messages.fullText, { max: DIRECTED_MAX_CAST })}</span>
      </div>
    );
  }

  const drop = {
    onDragOver: (event: React.DragEvent) => {
      if (!uploadOff) {
        event.preventDefault();
        setIsDragging(true);
      }
    },
    onDragLeave: () => setIsDragging(false),
    onDrop: (event: React.DragEvent) => {
      event.preventDefault();
      setIsDragging(false);
      if (!uploadOff) {
        onFiles(Array.from(event.dataTransfer.files));
      }
    },
  };

  const buttons = (
    <div className={styles.row}>
      <SMLButton
        onClick={() => input.current?.click()}
        disabled={uploadOff}
        title={uploadLocked ? intl.formatMessage(messages.uploadNeedsConsent) : undefined}
      >
        <span className={styles.subDesktop}>{intl.formatMessage(messages.upload)}</span>
        <span className={styles.subPhone}>{intl.formatMessage(messages.uploadPhone)}</span>
      </SMLButton>
      <SMLButton
        icon={sparkle}
        onClick={onMakePicture}
        disabled={disabled || noPicturesLeft}
        title={noPicturesLeft ? intl.formatMessage(messages.noPicturesLeft) : undefined}
      >
        {intl.formatMessage(messages.makePicture)}
      </SMLButton>
      {artistPicture !== null && (
        <SMLButton
          icon={<img className={styles.artistPictureThumb} src={artistPicture.url} alt="" />}
          onClick={onUseArtistPicture}
          disabled={disabled}
          title={intl.formatMessage(messages.useArtistPictureHint)}
        >
          {intl.formatMessage(messages.useArtistPicture)}
        </SMLButton>
      )}
      <input
        ref={input}
        type="file"
        hidden
        multiple
        accept={CAST_UPLOAD_TYPES.join(',')}
        data-testid="cast-file-input"
        onChange={(event) => {
          onFiles(Array.from(event.target.files ?? []));
          event.target.value = '';
        }}
      />
    </div>
  );

  const uploadHint = uploadLocked && !disabled && (
    <span className={styles.hint}>{intl.formatMessage(messages.uploadNeedsConsent)}</span>
  );

  if (variant === 'empty') {
    return (
      <div
        className={clsx(styles.empty, isDragging && styles.emptyDrag)}
        onDragOver={drop.onDragOver}
        onDragLeave={drop.onDragLeave}
        onDrop={drop.onDrop}
      >
        <b>{intl.formatMessage(messages.emptyTitle)}</b>
        <span className={styles.hint}>{intl.formatMessage(messages.emptyText)}</span>
        {buttons}
        {uploadHint}
        {noPicturesLeft && <span className={styles.hint}>{intl.formatMessage(messages.noPicturesLeft)}</span>}
      </div>
    );
  }
  return (
    <div
      className={clsx(styles.add, isDragging && styles.addDrag)}
      onDragOver={drop.onDragOver}
      onDragLeave={drop.onDragLeave}
      onDrop={drop.onDrop}
    >
      <span className={styles.addIcon} aria-hidden>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </span>
      <span className={styles.addTitle}>{intl.formatMessage(messages.addTitle)}</span>
      <span className={styles.addText}>{intl.formatMessage(messages.addText)}</span>
      {buttons}
      {uploadHint}
      {noPicturesLeft && <span className={styles.hint}>{intl.formatMessage(messages.noPicturesLeft)}</span>}
    </div>
  );
};

export const CastAddCard = TypedMemo(CastAddCardComponent);
